import React, { useState } from 'react';
import { Calendar, Clock, ChevronRight, Search } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  readTime: string;
  tags: string[];
  category: string;
}

const BlogSection: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: 'The Mathematics of Product Management: A Data-Driven Approach',
      excerpt: 'Exploring how mathematical principles can enhance product decision-making and strategy development.',
      content: `
        <h2>Introduction</h2>
        <p>Product management and mathematics may seem like distant fields, but they share fundamental principles of logic, optimization, and problem-solving.</p>
        
        <h2>The Calculus of User Needs</h2>
        <p>Just as calculus helps us understand rates of change, product management requires understanding how user needs evolve over time. The derivative of user satisfaction with respect to feature implementation can guide our prioritization.</p>
        
        <h2>Statistical Thinking in Product Decisions</h2>
        <p>A/B testing, user segmentation, and conversion rate optimization all rely on statistical principles. Understanding confidence intervals and statistical significance helps us make better decisions.</p>
        
        <h2>Linear Programming for Resource Allocation</h2>
        <p>With limited resources and competing priorities, linear programming can help optimize our product roadmap for maximum impact.</p>
        
        <h2>Conclusion</h2>
        <p>By applying mathematical thinking to product management, we can make more rational, data-driven decisions that lead to better outcomes.</p>
      `,
      date: '2024-01-15',
      readTime: '5 min read',
      tags: ['Product Management', 'Data Science', 'Mathematics'],
      category: 'Strategy'
    },
    {
      id: 2,
      title: 'Building User-Centric Products: The Golden Ratio of UX',
      excerpt: 'How the golden ratio and mathematical harmony can guide user experience design decisions.',
      content: `
        <h2>The Golden Ratio in Design</h2>
        <p>The golden ratio (φ ≈ 1.618) has been used in art and architecture for centuries. Can it also guide our product design decisions?</p>
        
        <h2>Applying Mathematical Principles to UX</h2>
        <p>From layout proportions to timing animations, mathematical principles can create more harmonious user experiences.</p>
        
        <h2>Case Studies</h2>
        <p>Examining successful products that have applied mathematical thinking to their design process.</p>
      `,
      date: '2024-01-10',
      readTime: '4 min read',
      tags: ['UX Design', 'Product Design', 'Mathematics'],
      category: 'Design'
    },
    {
      id: 3,
      title: 'Probability Theory in Risk Assessment',
      excerpt: 'Using probability and statistics to better assess and mitigate product development risks.',
      content: `
        <h2>Understanding Risk in Product Development</h2>
        <p>Every product decision involves uncertainty. Probability theory gives us tools to quantify and manage this uncertainty.</p>
        
        <h2>Bayesian Thinking for Product Managers</h2>
        <p>How updating our beliefs with new evidence can lead to better product decisions.</p>
        
        <h2>Monte Carlo Simulations for Roadmap Planning</h2>
        <p>Using statistical simulations to better estimate timelines and resource needs.</p>
      `,
      date: '2024-01-05',
      readTime: '6 min read',
      tags: ['Risk Management', 'Statistics', 'Planning'],
      category: 'Strategy'
    },
    {
      id: 4,
      title: 'The Algorithm of Growth: Mathematical Models for User Acquisition',
      excerpt: 'Exploring mathematical models that can help predict and optimize user growth patterns.',
      content: `
        <h2>Growth Curves and S-Curves</h2>
        <p>Understanding the mathematics behind user adoption and market saturation.</p>
        
        <h2>Viral Coefficients and Network Effects</h2>
        <p>The mathematical foundation of viral growth and how to calculate your product's viral potential.</p>
        
        <h2>Optimizing Conversion Funnels</h2>
        <p>Using statistical analysis to identify and fix bottlenecks in your conversion funnel.</p>
      `,
      date: '2023-12-28',
      readTime: '7 min read',
      tags: ['Growth', 'Analytics', 'Metrics'],
      category: 'Growth'
    }
  ];

  const categories = ['All', 'Strategy', 'Design', 'Growth'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const selectedPostData = blogPosts.find(post => post.id === selectedPost);

  if (selectedPost && selectedPostData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-beige-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <button
            onClick={() => setSelectedPost(null)}
            className="mb-8 flex items-center text-beige-700 hover:text-beige-900 transition-colors"
          >
            <ChevronRight size={20} className="mr-2 rotate-180" />
            Back to Blog
          </button>

          <article className="bg-white rounded-lg shadow-sm border border-beige-200 p-8">
            <header className="mb-8">
              <h1 className="text-4xl font-light text-gray-900 mb-4">
                {selectedPostData.title}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar size={14} className="mr-1" />
                  {selectedPostData.date}
                </div>
                <div className="flex items-center">
                  <Clock size={14} className="mr-1" />
                  {selectedPostData.readTime}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {selectedPostData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-beige-100 text-beige-800 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div
              className="prose prose-beige max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedPostData.content }}
            />
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-light text-gray-900 mb-4 flex items-center justify-center">
            <span className="font-math mr-4">π</span>
            Blog & Insights
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Thoughts on product management, mathematics, and the intersection of technology and business.
          </p>
          <p className="text-lg font-math text-beige-600 mt-4">
            Insights = ∫(Experience × Analysis) dt
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-beige-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    selectedCategory === category
                      ? 'bg-beige-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-beige-100 border border-beige-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-lg shadow-sm border border-beige-200 overflow-hidden hover:shadow-md transition-shadow duration-300 cursor-pointer"
              onClick={() => setSelectedPost(post.id)}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-beige-600">
                    {post.category}
                  </span>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock size={14} className="mr-1" />
                    {post.readTime}
                  </div>
                </div>
                
                <h3 className="text-xl font-medium text-gray-900 mb-3 line-clamp-2">
                  {post.title}
                </h3>
                
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar size={14} className="mr-1" />
                    {post.date}
                  </div>
                  
                  <div className="flex gap-2">
                    {post.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-beige-100 text-beige-700 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No articles found matching your criteria.</p>
          </div>
        )}

        {/* Mathematical Footer */}
        <div className="mt-16 text-center">
          <div className="inline-block p-6 bg-white rounded-lg border border-beige-200">
            <p className="font-math text-beige-600 text-sm">
              Knowledge = ∫(Curiosity × Learning) dt from 0 to ∞
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogSection;
